/**
 * One protection truth: how many open positions lack verified exchange-resting
 * protection, read from the exchange-ledger deployment rows [mv2-D2][S1][S4].
 *
 * The top bar, the mobile nav, the Dashboard and the execution truth strip all
 * read `/monitoring/deployments` (include_closed=false). `useUnprotectedCount`
 * returned `{count: 0, unknown: false}` while loading (checking read as zero),
 * threw on `{}`, produced `'01'` from a string `'1'`, and had no age check.
 *
 * | liveQueryState phase   | reading                                                   | current |
 * |------------------------|-----------------------------------------------------------|---------|
 * | checking / idle        | checking                                                  | no      |
 * | refreshing             | known with the last count when it is above 0 (exposure    | no      |
 * |                        | stays loud), otherwise checking                           |         |
 * | error-* / overdue*     | unknown, with the last-known count when the kept rows are | no      |
 * |                        | well-formed                                               |         |
 * | live                   | known (unknown when the rows are malformed)               | yes     |
 *
 * `known` is not `current` [M1 architect, 2026-09-25]: a cached answer older
 * than its maximum age, just after mount or a return to the tab, stays known
 * while its BOOK-WIDE count is above 0, so a reader of one deployment's row
 * (the dossier) must check `current` before showing that row's kept state.
 *
 * Rows that are not an array, or a count that is not a safe integer ≥ 0, are
 * unknown ("malformed"), never a number. Pure: runtime imports relative `.ts`.
 */
import type { MonitoringDeploymentRow } from '@/api/types';
import type { Tone } from '@/lib/colors';

import { liveQueryState, type FreshnessClock, type LiveQueryLike } from '../../lib/liveQueryState.ts';

export const PROTECTION_LABELS = { what: 'protection rows', panel: 'Protection rows' } as const;

/** The workspace that owns an unprotected position. */
export const PROTECTION_OWNER_HREF = '/monitoring?tab=reconciliation';

export const MALFORMED_PROTECTION =
  'protection rows were malformed (expected deployment rows with a whole-number unprotected_positions)';

export interface ProtectionTruth {
  state: 'checking' | 'known' | 'unknown';
  /**
   * True only when the reading comes from a live answer: within its maximum
   * age and well-formed. Never while checking, never while a cached answer
   * refreshes (even when that keeps an exposure known and loud), and never
   * when the rows failed, hung or arrived malformed.
   */
  current: boolean;
  /** Unprotected positions, only when known. */
  count: number | null;
  /** The last well-formed count, kept through a failed or hung read. */
  lastKnownCount: number | null;
  /** Rows whose protection is unverified (reconciliation not matched), when rows are readable. */
  unverifiedCount: number | null;
  /** Distinct symbols with an unprotected position, from the rows read. */
  symbols: string[];
  /** Why the reading is unknown or checking (the liveQueryState message), else null. */
  message: string | null;
  /** When the rows behind the reading arrived. */
  asOfMs: number | null;
}

interface Tally { count: number; unverified: number; symbols: string[] }

/** Sum and symbols over well-formed rows, or null when anything is malformed. */
function tally(rows: unknown): Tally | null {
  if (!Array.isArray(rows)) return null;
  let count = 0;
  let unverified = 0;
  const symbols: string[] = [];
  for (const row of rows as unknown[]) {
    if (row === null || typeof row !== 'object') return null;
    const r = row as Partial<MonitoringDeploymentRow>;
    const n: unknown = r.unprotected_positions;
    if (typeof n !== 'number' || !Number.isSafeInteger(n) || n < 0) return null;
    count += n;
    if (r.protection_state === 'unverified') unverified += 1;
    if (n > 0 && typeof r.symbol === 'string' && !symbols.includes(r.symbol)) symbols.push(r.symbol);
  }
  return { count, unverified, symbols };
}

/** Total unprotected positions over the rows, or null when a row is malformed. */
export function sumUnprotected(rows: unknown): number | null {
  return tally(rows)?.count ?? null;
}

/** `1 position without verified exchange protection` (byte-identical to the old Dashboard item). */
export function protectionHeadline(n: number): string {
  return `${n} position${n === 1 ? '' : 's'} without verified exchange protection`;
}

/** `PROTECTION UNKNOWN: ‹message›`, plus `; last known ‹n› unprotected` when a last count exists. */
export function protectionUnknownText(p: Pick<ProtectionTruth, 'message' | 'lastKnownCount'>): string {
  const last = p.lastKnownCount === null ? '' : `; last known ${p.lastKnownCount} unprotected`;
  return `PROTECTION UNKNOWN: ${p.message ?? 'protection rows could not be read'}${last}`;
}

/** The Monitoring Overview's Protection pill from the one protection truth: the
 *  exchange-ledger deployment rows, never the reconciliation verdict and never
 *  the paper table. Checking and unknown are never a count; a last-known
 *  exposure keeps it red; rows that cannot confirm protection read Unverified. */
export function protectionPill(p: ProtectionTruth): { tone: Tone; label: string; detail: string } {
  if (p.state === 'checking') {
    return { tone: 'muted', label: 'Checking', detail: p.message ?? 'Checking protection rows…' };
  }
  if (p.state === 'unknown') {
    return { tone: (p.lastKnownCount ?? 0) > 0 ? 'danger' : 'warn', label: 'Unknown', detail: protectionUnknownText(p) };
  }
  const n = p.count ?? 0;
  if (n > 0) {
    return {
      tone: 'danger',
      label: `${n} unprotected`,
      detail: `${p.symbols.join(' · ')} — no verified exchange-resting stop covers the full size.`,
    };
  }
  const u = p.unverifiedCount ?? 0;
  if (u > 0) {
    return {
      tone: 'warn',
      label: 'Unverified',
      detail: `${u} deployment${u === 1 ? '' : 's'} cannot confirm exchange protection until reconciliation matches.`,
    };
  }
  return {
    tone: 'info',
    label: 'Verified',
    detail: 'Every open position has a matched exchange-resting stop, or its deployment is measured flat.',
  };
}

export function protectionTruth(
  q: LiveQueryLike<readonly MonitoringDeploymentRow[]>,
  freshness?: FreshnessClock & { maxAgeMs: number },
): ProtectionTruth {
  const s = liveQueryState(q, PROTECTION_LABELS, freshness);
  const kept = s.data === undefined ? null : tally(s.data);
  const base = {
    unverifiedCount: kept?.unverified ?? null,
    symbols: kept?.symbols ?? [],
    asOfMs: s.lastGoodAtMs,
    current: false,
  };
  switch (s.phase) {
    case 'idle':
    case 'checking':
      return { ...base, state: 'checking', count: null, lastKnownCount: null, message: s.message };
    case 'refreshing':
      // A cached answer on mount or return: a kept exposure stays loud, but the
      // reading is not current (a per-deployment reader must not show its row).
      if (kept !== null && kept.count > 0) {
        return { ...base, state: 'known', count: kept.count, lastKnownCount: kept.count, message: null };
      }
      return { ...base, state: 'checking', count: null, lastKnownCount: kept?.count ?? null, message: s.message };
    case 'error-before-data':
    case 'error-after-data':
    case 'overdue-before-data':
    case 'overdue':
      return { ...base, state: 'unknown', count: null, lastKnownCount: kept?.count ?? null, message: s.message };
    case 'live':
      if (kept === null) {
        return { ...base, state: 'unknown', count: null, lastKnownCount: null, message: MALFORMED_PROTECTION };
      }
      return { ...base, state: 'known', current: true, count: kept.count, lastKnownCount: kept.count, message: null };
  }
}
